import 'react-tooltip/dist/react-tooltip.css'
import 'bootstrap/dist/css/bootstrap.min.css';
import { useState, useEffect } from "react";
import { Row, Col, Container, Button, Modal, Form, Stack, Alert, Spinner, InputGroup } from "react-bootstrap";
import { NumericValidationRules, parseNumericInput } from '../../utils/InputValidator';
import { getConsignmentDeckSpeciesWeights, updateConsignmentDeckSpecieWeights } from '../../api/apiAccess';
import ReadWriteFormControl from '../ReadWriteElements/ReadWriteFormControl';
import ReadWriteInputGroup from '../ReadWriteElements/ReadWriteInputGroup';
import { StringIsNullUndefinedOrEmpty } from '../../utils/StringUtils';

const AverageDeckWeightModal = ({ consignmentID, showModal, setShowModal }) => {
    const [loading, setLoading] = useState(false);
    const [loadingError, setLoadingError] = useState(false);

    const [headerText, setHeaderText] = useState("Average Deck Weight");
    const [consignmentDeckSpeciesWeights, setConsignmentDeckSpeciesWeights] = useState([]);

    const [saving, setSaving] = useState(false);
    const [showSaveErrorMessage, setShowSaveErrorMessage] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const numericValidationRules = new NumericValidationRules({ allowLeadingZerosForWholeNumbers: true, allowNegative: false, allowDecimal: true });

    useEffect(() => {
        if (!showModal) return;

        const PopulatePage = async () => {
            setLoading(true);
            setLoadingError(false);
            setShowSaveErrorMessage(false);
            setShowSuccessMessage(false);
            setHeaderText("Average Deck Weight");

            const response = await getConsignmentDeckSpeciesWeights(consignmentID);

            if (response?.status === 200) {
                setConsignmentDeckSpeciesWeights(response.data);
            } else {
                setLoadingError(true);
            }

            setLoading(false);
        };

        PopulatePage();
    }, [consignmentID, showModal]);

    const handleSave = () => {
        if (!saving) {
            setSaving(true);
            setShowSaveErrorMessage(false);
            setShowSuccessMessage(false);

            const save = async () => {
                // we do'nt need to send through items that dont have a weight since we dlete al lrows then re-add in the database
                const flatSpeciesWeights = consignmentDeckSpeciesWeights.flatMap(deck =>
                    deck.species.filter(species => !StringIsNullUndefinedOrEmpty(species.averageWeight)).map(species => ({
                        ConsignmentDeckID: deck.consignmentDeckID,
                        SpeciesID: species.id,
                        AverageWeight: species.averageWeight
                    }))
                );

                const result = await updateConsignmentDeckSpecieWeights(consignmentID, flatSpeciesWeights);

                if (result?.status == 200) {
                    setShowSuccessMessage(true);

                    setTimeout(() => {
                        setShowSuccessMessage(false);
                    }, 15000);
                }
                else {
                    setShowSaveErrorMessage(true);
                }

                setSaving(false);
            }
            save();
        }
    };

    const updateDeckSpeciesWeight = (consignmentDeckID, speciesID, averageWeight) => {
        setConsignmentDeckSpeciesWeights(prevDecks => {
            return prevDecks.map(deck => {
                if (deck.consignmentDeckID !== consignmentDeckID) return deck;
                const updatedSpecies = deck.species.map(species => {
                    if (species.id !== speciesID) return species;
                    return {
                        ...species,
                        averageWeight: averageWeight,
                    };
                });

                return {
                    ...deck,
                    species: updatedSpecies,
                };
            });
        });
    };

    return (
        <Modal show={showModal} size='md' onHide={() => setShowModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>{headerText}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container>
                    {
                        loading === true && (
                            <div className="text-center pt-5 pb-5">
                                <Spinner className="mx-auto primary-spinner" />
                                <div className="mx-auto">Loading deck weights...</div>
                            </div>
                        )
                    }
                    {
                        !loading && !showSaveErrorMessage &&
                        <>
                            <Row className='mb-1'>
                                <Col className='modal-header-label' xs={4}>Deck</Col>
                                <Col className='modal-header-label' xs={4}>Species</Col>
                                <Col className='modal-header-label' xs={4}>Weight (kg)</Col>
                            </Row>
                            {
                                consignmentDeckSpeciesWeights?.map(deck =>
                                    <Row className='gray-border-top'>
                                        <Col xs={12}>
                                            <Row className='mb-2 mt-2'>
                                                <Col xs={4} className='dr-text'>{deck.deckName}</Col>
                                                <Col xs={8}>
                                                    {
                                                        deck.species?.map((species, index) => (
                                                            <Row key={species.id} className={`pb-2 pt-2 ${index !== 0 ? 'gray-border-top' : ''}`}>
                                                                <Col xs={6} className='dr-text'>{species.speciesName}</Col>
                                                                <Col xs={6}>
                                                                    <Form.Group>
                                                                        <ReadWriteInputGroup readonlyclasses='dr-text'>
                                                                            <Form.Control
                                                                                id={`speciesWeight_${deck.consignmentDeckID}_${species.id}`}
                                                                                value={species.averageWeight}
                                                                                onChange={(e) => {
                                                                                    e.preventDefault();
                                                                                    parseNumericInput(e, numericValidationRules);
                                                                                    updateDeckSpeciesWeight(deck.consignmentDeckID, species.id, e.target.value);
                                                                                }}
                                                                                className="form-control inputGroupWithAppend errorLeft"
                                                                                disabled={saving}
                                                                            />
                                                                            <InputGroup.Text className={`inputGroupAppend`}>kg</InputGroup.Text>
                                                                        </ReadWriteInputGroup>
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>
                                                        ))
                                                    }
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                )
                            }
                        </>
                    }
                    {
                        !loading && loadingError &&
                        <Row>
                            <Col>
                                <Alert variant='danger'>Something went wrong while fetching weights.</Alert>
                            </Col>
                        </Row>
                    }
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <Container>
                    {
                        showSaveErrorMessage ?
                            <Row>
                                <Col>
                                    <Alert variant='danger'>Something went wrong while saving weights.</Alert>
                                </Col>
                            </Row>
                            : null
                    }
                    {
                        showSuccessMessage ?
                            <Row>
                                <Col>
                                    <Alert variant='success'>Deck weights saved successfully.</Alert>
                                </Col>
                            </Row>
                            : null
                    }
                    <Row>
                        <Col>
                            <Stack direction='horizontal' gap={3}>
                                <Button variant="primary" onClick={() => handleSave()}>Save
                                    {
                                        saving &&
                                        <Spinner style={{ width: '20px', height: '20px', marginLeft: "10px", display: "inline-block" }} />
                                    }
                                </Button>
                            </Stack>
                        </Col>
                    </Row>
                </Container>
            </Modal.Footer>
        </Modal >
    )
}

export default AverageDeckWeightModal;